import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0 });

export function InvoiceDialog({ open, onOpenChange, patient, onSaved }) {
  const { money } = useAuth();
  const [lines, setLines] = useState([emptyLine()]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setLines([emptyLine()]); setDiscount(0); setTax(0); setNotes(""); } }, [open]);

  const setLine = (i, k, v) => setLines((l) => l.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const taxable = Math.max(0, subtotal - (Number(discount) || 0));
    const taxAmt = taxable * (Number(tax) || 0) / 100;
    return { subtotal, taxAmt, total: taxable + taxAmt };
  }, [lines, discount, tax]);

  const save = async () => {
    const valid = lines.filter((l) => l.description.trim());
    if (valid.length === 0) { toast.error("Add at least one line item"); return; }
    setBusy(true);
    try {
      await api.post("/clinic/invoices", {
        patient_id: patient.id,
        lines: valid.map((l) => ({ description: l.description, quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0 })),
        discount: Number(discount) || 0, tax_percent: Number(tax) || 0, notes,
      });
      toast.success("Invoice created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-heading">New invoice — {patient?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {lines.map((l, i) => (
            <div key={i} data-testid={`inv-line-${i}`} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-6 space-y-1"><Label className="text-xs">Description</Label><Input data-testid={`inv-desc-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Qty</Label><Input type="number" step="any" data-testid={`inv-qty-${i}`} value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></div>
              <div className="col-span-3 space-y-1"><Label className="text-xs">Unit price</Label><Input type="number" step="any" data-testid={`inv-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} /></div>
              <div className="col-span-1"><Button size="icon" variant="ghost" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="h-9 w-9 hover:text-destructive" data-testid={`inv-remove-${i}`}><Trash size={14} /></Button></div>
            </div>
          ))}
          <Button size="sm" variant="outline" data-testid="add-inv-line" onClick={() => setLines((l) => [...l, emptyLine()])} className="gap-1.5"><Plus size={14} /> Add line</Button>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1"><Label className="text-xs">Discount</Label><Input type="number" step="any" data-testid="inv-discount" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Tax %</Label><Input type="number" step="any" data-testid="inv-tax" value={tax} onChange={(e) => setTax(e.target.value)} /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          <div className="bg-muted/50 rounded-md p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tnums">{money(totals.subtotal)}</span></div>
            {Number(discount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="tnums">−{money(Number(discount))}</span></div>}
            {Number(tax) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="tnums">{money(totals.taxAmt)}</span></div>}
            <div className="flex justify-between font-semibold border-t border-border pt-1 mt-1"><span>Total</span><span className="tnums" data-testid="inv-total">{money(totals.total)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-invoice-button" className="bg-primary hover:bg-primary/90">{busy ? "Creating…" : "Create invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const METHOD_LABEL = { cash: "Cash", card: "Card", upi: "UPI", bank_transfer: "Bank transfer", insurance: "Insurance", other: "Other" };

export function PaymentDialog({ open, onOpenChange, invoice, methods = [], onSaved }) {
  const { money } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && invoice) { setAmount(String(invoice.balance || "")); setMethod("cash"); setReference(""); } }, [open, invoice]);

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/clinic/invoices/${invoice.id}/payments`, { amount: amt, method, reference });
      toast.success("Payment recorded");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-heading">Record payment</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">Invoice {invoice.invoice_no} · Balance due <span className="font-medium text-foreground">{money(invoice.balance)}</span></p>
          <div className="space-y-1.5"><Label>Amount</Label><Input type="number" step="any" data-testid="pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{METHOD_LABEL[m] || m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Reference <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="pay-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Txn / receipt no" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-payment-button" className="bg-primary hover:bg-primary/90">{busy ? "Saving…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
